/**
 * Chat presence ("user collision") — who else has this conversation open.
 *
 * Every agent tab that opens a chat writes ONE small node to RTDB:
 *
 *   /presenceAndCollisions/{extensionName}/{orgKey}/{convKey}/{uid}/{tabId}
 *     = { n: name, p: photo, s: 'viewing' | 'typing', t: serverTime }
 *
 * No email in the node: the folder is readable by anyone who knows the path
 * (see the rule below), so only what the chip shows is stored.
 *
 * Why RTDB and not Firestore: `onDisconnect()` lets the server delete the
 * node by itself when the tab closes or the network drops, so a crashed tab
 * does not leave a ghost avatar behind. As a second safety net the reader
 * ignores nodes whose `t` is older than STALE_AFTER_MS; a heartbeat refreshes
 * `t` while the tab is alive.
 *
 * Keyed per TAB under the uid so closing one of two tabs of the same person
 * does not remove them from the list. The reader groups by uid.
 *
 * Security rule — ALREADY LIVE in the console (not in this repo):
 *   "presenceAndCollisions": { "$appName": { "$orgId": { "$pageID": {
 *     ".read": true,
 *     "$uid": { ".read": true, ".write": "$uid === auth.uid" }
 *   }}}}
 * RTDB rules cascade downward, so the `.write` on `$uid` also allows the
 * per-tab child nodes underneath it.
 */

import {
  ref,
  set,
  remove,
  onValue,
  onDisconnect,
  serverTimestamp,
  type DatabaseReference,
  type Unsubscribe,
} from 'firebase/database';
import { rtdb } from '@/config/firebase';
import { sanitizeForPath } from './rtdb';

export type PresenceStatus = 'viewing' | 'typing';

/** Raw node shape written to RTDB. Short keys keep the payload tiny. */
export interface PresenceNode {
  /** display name */
  n: string;
  /** photo url */
  p?: string;
  /** status */
  s: PresenceStatus;
  /** server timestamp (ms) of the last write */
  t: number;
}

/** One person, after grouping their tabs together. */
export interface PresenceUser {
  uid: string;
  name: string;
  photoURL?: string;
  /** true when ANY of this person's tabs reports 'typing' */
  typing: boolean;
}

/** Nodes with `t` older than this are treated as gone (crashed tab whose
 *  onDisconnect never fired). Heartbeat runs at half this interval. */
export const STALE_AFTER_MS = 2 * 60 * 1000;
export const HEARTBEAT_MS = 60 * 1000;

/** One id per browser tab, made once. Two tabs of the same user → two nodes. */
let tabId: string | null = null;
export function getTabId(): string {
  if (!tabId) {
    tabId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  }
  return tabId;
}

export interface PresenceScope {
  extensionName: string;
  /** org id when the app has one; callers fall back to the effective uid */
  orgKey: string;
  conversationId: string;
}

/** Path of the conversation folder (all people in this chat). */
export function presenceConvPath(scope: PresenceScope): string {
  return `/presenceAndCollisions/${sanitizeForPath(scope.extensionName)}/${sanitizeForPath(scope.orgKey)}/${sanitizeForPath(scope.conversationId)}`;
}

/** Path of MY node inside the conversation folder: `{uid}/{tabId}`. */
export function presenceEntryPath(scope: PresenceScope, uid: string): string {
  return `${presenceConvPath(scope)}/${uid}/${getTabId()}`;
}

/** Raw folder shape as read from RTDB: `{ uid: { tabId: node } }`. */
export type PresenceFolder = Record<string, Record<string, PresenceNode>>;

export interface PresenceIdentity {
  uid: string;
  name: string;
  photoURL?: string;
}

/**
 * Handle for my own presence in one conversation. Create with `joinPresence`,
 * call `setStatus` / `heartbeat` while alive, `leave` when done.
 */
export interface PresenceHandle {
  setStatus(status: PresenceStatus): Promise<void>;
  heartbeat(): Promise<void>;
  leave(): Promise<void>;
}

/**
 * Which join currently owns each entry path. Two joins for the same path
 * happen back-to-back when React re-runs an effect (StrictMode in dev, or a
 * fast dep flip): the FIRST one's late `leave()` must not delete the node the
 * SECOND one just wrote at the same `{uid}/{tabId}` path. A handle only acts
 * while it is still the owner.
 */
const owners = new Map<string, object>();

/**
 * Write my node and arm onDisconnect so the server removes it if this tab
 * vanishes. Returns a handle for later updates. Never throws — presence is
 * cosmetic and must not break the chat if the rule is missing.
 */
export async function joinPresence(scope: PresenceScope, me: PresenceIdentity): Promise<PresenceHandle> {
  const path = presenceEntryPath(scope, me.uid);
  const entryRef: DatabaseReference = ref(rtdb, path);
  const token = {};
  owners.set(path, token);
  const owned = () => owners.get(path) === token;
  let current: PresenceStatus = 'viewing';
  let left = false;

  // Always write the WHOLE node, never a partial `update()`: after a network
  // blip the server's onDisconnect has already deleted the node, and a partial
  // update would recreate it as `{ t }` with no name ("Someone viewing").
  // No email in the node — the folder is world-readable by rule.
  const fullNode = (): PresenceNode => ({
    n: me.name,
    ...(me.photoURL && { p: me.photoURL }),
    s: current,
    // serverTimestamp() is a placeholder object the server replaces with ms.
    t: serverTimestamp() as unknown as number,
  });

  const write = async (what: string): Promise<void> => {
    try {
      await set(entryRef, fullNode());
    } catch (err) {
      console.warn(`[ChatPresence] ${what} failed`, err);
    }
  };

  try {
    // Arm the cleanup BEFORE the first write so a disconnect in between
    // still leaves nothing behind.
    await onDisconnect(entryRef).remove();
    await set(entryRef, fullNode());
  } catch (err) {
    console.warn('[ChatPresence] join failed (rule missing?)', err);
  }

  // Reconnect → the server already ran our onDisconnect (node gone) and
  // forgot it; the SDK does NOT re-send it. Arm it again FIRST, then put the
  // node back, so the rejoined node is still removed when this tab closes.
  const rejoin = async (): Promise<void> => {
    try {
      await onDisconnect(entryRef).remove();
    } catch (err) {
      console.warn('[ChatPresence] re-arm failed', err);
    }
    // leave() or a newer join may have happened while the re-arm was in flight.
    if (left || !owned()) return;
    await write('rejoin');
  };

  let seenConnected = false;
  const stopConnected = onValue(ref(rtdb, '.info/connected'), (snap) => {
    const connected = snap.val() === true;
    if (connected && seenConnected && !left && owned()) void rejoin();
    if (connected) seenConnected = true;
  }, () => { /* rule/offline noise — presence is cosmetic */ });

  return {
    async setStatus(status) {
      if (left || !owned() || status === current) return;
      current = status;
      await write('status write');
    },
    async heartbeat() {
      if (left || !owned()) return;
      await write('heartbeat');
    },
    async leave() {
      if (left) return;
      left = true;
      stopConnected();
      // Superseded by a newer join at the same path → that join owns the node
      // (and re-armed onDisconnect). Touching it would delete their entry.
      if (!owned()) return;
      owners.delete(path);
      try {
        await onDisconnect(entryRef).cancel();
        await remove(entryRef);
      } catch (err) {
        console.warn('[ChatPresence] leave failed', err);
      }
    },
  };
}

/**
 * Turn the raw `{ uid: { tabId: node } }` folder into one row per person.
 * Drops my own uid and any tab node older than STALE_AFTER_MS.
 *
 * `now` must be SERVER time (Date.now() + serverTimeOffset) because `t` is a
 * server timestamp; a client clock a few minutes fast would otherwise treat
 * every colleague as stale. See `subscribeServerTimeOffset`.
 */
export function groupPresence(
  raw: PresenceFolder | null | undefined,
  myUid: string,
  now: number = Date.now(),
): PresenceUser[] {
  if (!raw) return [];
  const out: PresenceUser[] = [];
  for (const [uid, tabs] of Object.entries(raw)) {
    if (uid === myUid || !tabs || typeof tabs !== 'object') continue;
    let user: PresenceUser | null = null;
    for (const node of Object.values(tabs)) {
      if (!node || typeof node !== 'object') continue;
      // `t` can be a pending placeholder for a split second on the writer's own
      // client; readers only ever see the resolved number. Treat non-numbers as fresh.
      if (typeof node.t === 'number' && now - node.t > STALE_AFTER_MS) continue;
      if (user) {
        user.typing = user.typing || node.s === 'typing';
        if (!user.photoURL && node.p) user.photoURL = node.p;
      } else {
        user = {
          uid,
          name: node.n || 'Someone',
          photoURL: node.p,
          typing: node.s === 'typing',
        };
      }
    }
    if (user) out.push(user);
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * True when two grouped lists show the same thing. Lets the hook keep the
 * previous array (no re-render) when a heartbeat or the ageing timer produced
 * an identical result.
 */
export function samePresence(a: PresenceUser[], b: PresenceUser[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x.uid !== y.uid || x.name !== y.name || x.typing !== y.typing || x.photoURL !== y.photoURL) return false;
  }
  return true;
}

/**
 * Listen to the client→server clock offset (ms). Add it to Date.now() before
 * comparing against a server timestamp. Fires 0 first so callers never wait.
 */
export function subscribeServerTimeOffset(cb: (offsetMs: number) => void): Unsubscribe {
  cb(0);
  return onValue(
    ref(rtdb, '.info/serverTimeOffset'),
    (snap) => {
      const v = snap.val();
      cb(typeof v === 'number' && Number.isFinite(v) ? v : 0);
    },
    () => cb(0),
  );
}

/** "Iyappan is typing…" / "Iyappan and Vijay are typing…" / "3 people are typing…" */
export function typingLabel(users: PresenceUser[]): string | null {
  const typing = users.filter((u) => u.typing);
  if (typing.length === 0) return null;
  if (typing.length === 1) return `${typing[0].name} is typing a reply…`;
  if (typing.length === 2) return `${typing[0].name} and ${typing[1].name} are typing…`;
  return `${typing.length} people are typing…`;
}

/**
 * Listen to everyone in the conversation folder. Calls back with the raw map
 * on every change. Returns the unsubscribe function.
 */
export function subscribePresence(
  scope: PresenceScope,
  cb: (raw: PresenceFolder | null) => void,
): Unsubscribe {
  const convRef = ref(rtdb, presenceConvPath(scope));
  return onValue(
    convRef,
    (snap) => cb((snap.val() as PresenceFolder | null) ?? null),
    (err) => {
      console.warn('[ChatPresence] subscribe failed (rule missing?)', err);
      cb(null);
    },
  );
}
